import React from 'react';
import { Document } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import ChecklistCoverPage from './ChecklistCoverPage';
import ChecklistStatusDefinitionsPage from './ChecklistStatusDefinitionsPage';
import ChecklistTestCasesPage from './ChecklistTestCasesPage';
import { ProgramChecklist, ProgramChecklistItem } from '../../../utils/api/endpoints/checklists/checklistApi';

export interface ChecklistPDFData {
  checklist: ProgramChecklist;
  programName: string;
  orgName?: string;
  approvedDate?: string;
}

interface ChecklistPDFDocumentProps extends ChecklistPDFData {}

/**
 * Root @react-pdf/renderer Document wrapping all three pages:
 *   1. Cover
 *   2. Status Definitions
 *   3. Test Cases (auto-paginated)
 */
const ChecklistPDFDocument: React.FC<ChecklistPDFDocumentProps> = ({
  checklist,
  programName,
  orgName,
  approvedDate,
}) => {
  const items: ProgramChecklistItem[] = [...(checklist.items || [])].sort(
    (a, b) => (a.item_order ?? 0) - (b.item_order ?? 0)
  );

  return (
    <Document
      title={`Pentest Checklist – ${programName}`}
      author="Capture The Bug"
      subject="Pentest Checklist Export"
      creator="CTB Platform"
    >
      <ChecklistCoverPage
        programName={programName}
        orgName={orgName}
        approvedDate={approvedDate}
      />
      <ChecklistStatusDefinitionsPage />
      <ChecklistTestCasesPage
        items={items}
        programName={programName}
        orgName={orgName}
      />
    </Document>
  );
};

/**
 * Generates a PDF Blob for the given checklist data.
 * Call this from a button handler:
 *
 *   const blob = await generateChecklistPDF({ checklist, programName, orgName });
 *   const url = URL.createObjectURL(blob);
 *   const a = document.createElement('a');
 *   a.href = url; a.download = 'checklist.pdf'; a.click();
 *   URL.revokeObjectURL(url);
 */
export const generateChecklistPDF = async (data: ChecklistPDFData): Promise<Blob> => {
  const element = (
    <ChecklistPDFDocument
      checklist={data.checklist}
      programName={data.programName}
      orgName={data.orgName}
      approvedDate={data.approvedDate}
    />
  );
  return pdf(element).toBlob();
};

export default ChecklistPDFDocument;
